import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { validateResetToken, resetPassword } from "@/services/auth";
import { trackPasswordResetCompleted } from "@/analytics/analytics";
import { BrandLogo } from "@/components/BrandLogo";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    async function validateToken() {
      if (!token) {
        setTokenError("No reset token provided");
        setValidating(false);
        return;
      }

      try {
        const result = await validateResetToken(token);
        setEmail(result.email);
        setTokenValid(true);
      } catch (err: any) {
        setTokenError(err?.message || "Invalid or expired reset link");
      } finally {
        setValidating(false);
      }
    }

    validateToken();
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      toast({
        title: "Missing fields",
        description: "Please fill in all fields.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length <= 10) {
      toast({
        title: "Password too short",
        description: "Password must be more than 10 characters.",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Passwords do not match",
        description: "Please make sure both passwords match.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      await resetPassword(token, newPassword);
      trackPasswordResetCompleted();
      setResetSuccess(true);
      toast({
        title: "Success!",
        description: "Your password has been reset successfully.",
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (err: any) {
      toast({
        title: "Reset failed",
        description: err?.message || "Failed to reset password. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  if (validating) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <h1>
              <BrandLogo className="mx-auto h-11 w-auto" />
            </h1>
          </div>
          <Card className="w-full">
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground">Validating reset link...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <h1>
              <BrandLogo className="mx-auto h-11 w-auto" />
            </h1>
          </div>
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Invalid reset link</CardTitle>
              <CardDescription>This password reset link is not valid</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant="destructive">
                <AlertDescription>
                  {tokenError || "This reset link is invalid or has expired."}
                </AlertDescription>
              </Alert>
              <div className="text-sm text-muted-foreground">
                <p>Reset links expire after 30 minutes and can only be used once.</p>
              </div>
              <div className="space-y-2 pt-2">
                <Link to="/forgot-password">
                  <Button variant="default" className="w-full">
                    Request New Reset Link
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full">
                    Back to sign in
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (resetSuccess) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <h1>
              <BrandLogo className="mx-auto h-11 w-auto" />
            </h1>
          </div>
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Password reset complete</CardTitle>
              <CardDescription>Your password has been updated</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert>
                <AlertDescription>
                  Your password has been successfully reset. You can now sign in with your new
                  password.
                </AlertDescription>
              </Alert>
              <div className="pt-2">
                <Link to="/">
                  <Button className="w-full">Continue to sign in</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <h1>
            <BrandLogo className="mx-auto h-11 w-auto" />
          </h1>
          <p className="text-sm text-muted-foreground mt-2">Create a new password</p>
        </div>
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Reset password</CardTitle>
            <CardDescription>Enter a new password for {email}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">New password</label>
                <Input
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  placeholder="Enter new password (min 11 characters)"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  autoFocus
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Confirm password</label>
                <Input
                  type="password"
                  name="confirm-password"
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                />
              </div>
              <div className="text-xs text-muted-foreground">
                Password must be more than 10 characters long.
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Resetting password…" : "Reset password"}
              </Button>
              <div className="text-center pt-2">
                <Link to="/">
                  <Button variant="link" className="px-0 h-auto text-sm">
                    Back to sign in
                  </Button>
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
